import React, { useEffect, useState } from "react";
import { Alert, Table, Title } from "@mantine/core";
import { getDeviceLogs } from "../../utils/api-communication/RestApiCli";
import {
  DEVICE_LOG_SEVERITY_COLOR,
  deviceLogSeverity,
  parseDeviceLogsResponse,
} from "../../utils/deviceLogUtils";
import { DEVICE_LOG_SEVERITY_ICON } from "../../utils/deviceLogSeverityIcons";
import { formatDateAndTime } from "../../utils/CommonUtil";
import { useTranslation } from "react-i18next";
import { deviceLogMessageFormatter, DeviceLogs } from "../../model/DeviceLogs";
import { openConfirmation } from "../../utils/redux-utils/actions/actions";
import { useDispatch } from "react-redux";

export const importantLogs = [
  DeviceLogs.DownloadTriggered,
  DeviceLogs.AppDownloadSuccess,
  DeviceLogs.AppDownloadFailed,
  DeviceLogs.OSDownloadSuccess,
  DeviceLogs.OSDownloadFailed,
  DeviceLogs.RebootStarted,
  DeviceLogs.AppUpdateTriggered,
  DeviceLogs.OSUpdateTriggered,
  DeviceLogs.AppUpdateSuccess,
  DeviceLogs.AppUpdateFailed,
  DeviceLogs.OSUpdateSuccess,
  DeviceLogs.OSUpdateFailed,
];

export default function DeviceLogsViewer({
  pid,
  timeOffset,
  setActiveStepNo,
  setUpdateTimelineTimes,
  adbLogs = [],
}) {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const limit = 10;
  const dispatch = useDispatch();

  useEffect(() => {
    let intervalId;
    if (logs.length > 0) {
      intervalId = setInterval(() => {
        retrieveLogs();
      }, 10000);
    }
    return () => clearInterval(intervalId);
  }, [logs]);

  useEffect(() => {
    retrieveLogs();
  }, []);

  const retrieveLogs = () => {
    const pageSize = logs?.length > 0 ? 1 : limit;
    getDeviceLogs(pid, 1, pageSize, (response) => {
      const ls = parseDeviceLogsResponse(response).logs;
      if (pageSize === 1 && ls.length > 0 && logs[0]._id === ls[0]._id) {
        console.log("No new logs");
      } else {
        getDeviceLogs(pid, 1, limit, (response) => {
          setNewLogs(parseDeviceLogsResponse(response).logs);
        });
      }
    });

    const showFailedDialog = (logMessage) => {
      dispatch(
        openConfirmation(
          "Fail!",
          deviceLogMessageFormatter(logMessage),
          () => {
            console.log("User is active");
          },
          false,
          t("close"),
          "Retry",
        ),
      );
    };

    const logMessageTrigger = (logs) => {
      const stepNo = [0];
      for (const i in logs) {
        const logMessage = logs[i] as DeviceLogs;
        switch (logMessage) {
          case DeviceLogs.DownloadTriggered:
            stepNo.push(1);
            break;
          case DeviceLogs.AppDownloadSuccess:
          case DeviceLogs.OSDownloadSuccess:
            stepNo.push(2);
            break;
          case DeviceLogs.AppUpdateTriggered:
          case DeviceLogs.OSUpdateTriggered:
            stepNo.push(3);
            break;
          case DeviceLogs.RebootStarted:
          case DeviceLogs.AppUpdateSuccess: // not works
          case DeviceLogs.OSUpdateSuccess:
            stepNo.push(4);
            break;
          case DeviceLogs.AppDownloadFailed:
          case DeviceLogs.OSDownloadFailed:
            showFailedDialog(logMessage);
            break;
          case DeviceLogs.AppUpdateFailed:
          case DeviceLogs.OSUpdateFailed:
            showFailedDialog(logMessage);
            break;
          default:
            console.log("Unknown log message", logMessage);
            break;
        }
      }
      setActiveStepNo(Math.max(...stepNo));
    };

    const setNewLogs = (ls) => {
      const filteredLogs = [];
      ls.forEach((l) => {
        console.log("New log", l.data);
        const logMessage = l.data?.split(" ")[0] as DeviceLogs;
        if (timeOffset <= new Date(l.createdAt) && importantLogs.includes(logMessage)) {
          filteredLogs.push(logMessage);
          setUpdateTimelineTimes((prev) => [...prev, new Date(l.createdAt)]);
        }
        const severity = deviceLogSeverity(l);
        l.icon = DEVICE_LOG_SEVERITY_ICON[severity];
        l.color = DEVICE_LOG_SEVERITY_COLOR[severity];

        l.data = deviceLogMessageFormatter(l.data);
      });
      setLogs(ls);
      logMessageTrigger(filteredLogs);
    };
  };

  return (
    <>
      <Table withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th colSpan={2}>
              <Title order={3} style={{ textAlign: "center" }}>
                Device Logs
              </Title>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Time</Table.Th>
            <Table.Th>Log</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {logs?.map((element) => (
            <Table.Tr key={element.createdAt}>
              <Table.Td>{formatDateAndTime(element.createdAt)}</Table.Td>
              <Table.Td>
                <Alert icon={element.icon} color={element.color}>
                  {element.data}
                </Alert>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <div style={{ marginTop: 12 }}>
        <Title order={4} style={{ textAlign: "center" }}>
          ADB Logs
        </Title>
        <div
          style={{
            border: "1px solid var(--oculera-border)",
            borderRadius: 6,
            padding: 8,
            marginTop: 8,
          }}
        >
          <div style={{ maxHeight: 180, overflow: "auto", fontSize: 12, whiteSpace: "pre-wrap" }}>
            {adbLogs && adbLogs.length > 0 ? adbLogs.join("\n") : "No ADB logs yet"}
          </div>
        </div>
      </div>
    </>
  );
}
